/**
 * The predictive Back: the top of the app follows the finger while a Back
 * gesture is in progress, and commits or springs back when it ends (`D193`).
 *
 * Android 14+ reports a Back swipe as it happens (`native/back-gesture.ts`).
 * At its start, the same `decideBack` the button uses says what the gesture
 * would do; if that is closing a layer or leaving a screen, the thing that
 * would go is moved by the gesture's progress, Material 3's way:
 *
 * - a full-screen layer, or a screen leaving, shrinks toward 90%, shifts
 *   toward the side the swipe came from and rounds its corners, while the
 *   screen it would uncover slides home from under it;
 * - a sheet shrinks a little and sinks.
 *
 * The preview writes the INDIVIDUAL `scale` and `translate` properties (and
 * `border-radius`), never `transform`: the layers enter and leave on
 * `transform` (`ui/overlay/stack.ts`) and the screen stack animates
 * `transform` too, so on a commit the exit continues from exactly where the
 * finger left the layer — the two compose instead of one replacing the
 * other. A cancel springs every value back to rest and clears it.
 *
 * Nothing here decides what Back DOES. A commit runs the action the button
 * would have run, through the same `runBackAction`.
 */
import { animate } from "motion/react";
import type { BackEdge } from "../native";
import {
  animateCoverage,
  holdCoverage,
  isCovered,
  setCoverage,
} from "../ui/overlay/underlay";
import type { BackAction } from "./back-button";
import type { LayerKind } from "./layers";
import {
  previewScreen,
  previousScreen,
  screenElement,
  skipNextEnter,
} from "./screen-stack";
import type { Screen } from "./search";

export type Surface = "fade" | "push" | "rise" | "sheet";

export interface Frame {
  readonly radius: number;
  readonly scale: number;
  readonly x: number;
  readonly y: number;
}

/** Material's full-screen preview: 90% at the furthest, a nudge sideways. */
const SURFACE_MIN_SCALE = 0.9;
const SURFACE_SHIFT_PX = 24;
const SURFACE_RADIUS_PX = 32;
/** A sheet shrinks less and sinks instead. */
const SHEET_MIN_SCALE = 0.94;
const SHEET_SINK_PX = 32;
/** How far left the uncovered screen starts, matching the push's underlay. */
const UNDER_SHIFT_PERCENT = 6;

/** Where a surface sits at `progress` of a gesture from `edge`. Pure. */
export function previewFrame(
  surface: Surface,
  progress: number,
  edge: BackEdge
): Frame {
  const p = Math.min(1, Math.max(0, progress));
  if (surface === "sheet") {
    return {
      radius: 0,
      scale: 1 - (1 - SHEET_MIN_SCALE) * p,
      x: 0,
      y: SHEET_SINK_PX * p,
    };
  }
  let direction = 0;
  if (edge === "left") {
    direction = 1;
  } else if (edge === "right") {
    direction = -1;
  }
  return {
    radius: SURFACE_RADIUS_PX * p,
    scale: 1 - (1 - SURFACE_MIN_SCALE) * p,
    x: direction * SURFACE_SHIFT_PX * p,
    y: 0,
  };
}

/** The uncovered screen's offset at `progress`, in percent of its width. Pure. */
export function underShift(progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return -UNDER_SHIFT_PERCENT * (1 - p);
}

/** What a gesture will move, decided at its start. */
export type PreviewTarget =
  | {
      readonly element: HTMLElement;
      readonly kind: "layer";
      readonly surface: Surface;
      /** The layer covers the screen, so the screen slides home with it. */
      readonly uncovers: boolean;
    }
  | {
      readonly element: HTMLElement;
      readonly kind: "screen";
      readonly under: Screen | null;
    };

/** Which kinds of action a gesture previews; the rest only commit. */
export function previews(action: BackAction): boolean {
  return (
    action.kind === "history-back" ||
    action.kind === "close-layer" ||
    action.kind === "today"
  );
}

function surfaceOf(element: HTMLElement): Surface {
  const value = element.dataset.surface;
  return value === "push" || value === "rise" || value === "fade"
    ? value
    : "sheet";
}

/**
 * The target for `action` with `top` the layer on top and `screen` the
 * screen shown, or `null` when nothing should follow the finger.
 */
export function findTarget(
  action: BackAction,
  top: LayerKind,
  screen: Screen
): PreviewTarget | null {
  if (!previews(action)) {
    return null;
  }
  if (top !== "screen") {
    const element = document.querySelector<HTMLElement>(
      `[data-layer="${top}"]:not([data-ending-style])`
    );
    if (element === null) {
      return null;
    }
    const surface = surfaceOf(element);
    return {
      element,
      kind: "layer",
      surface,
      uncovers: surface !== "sheet" && isCovered(),
    };
  }
  const element = screenElement(screen);
  if (element === null) {
    return null;
  }
  const back = action.kind === "today" ? "today" : previousScreen();
  return {
    element,
    kind: "screen",
    under: back === null || back === screen ? null : back,
  };
}

function write(element: HTMLElement, frame: Frame): void {
  element.style.scale = String(frame.scale);
  element.style.translate = `${frame.x}px ${frame.y}px`;
  element.style.borderRadius = frame.radius > 0 ? `${frame.radius}px` : "";
  element.style.overflow = frame.radius > 0 ? "hidden" : "";
}

function clear(element: HTMLElement | null): void {
  if (element === null) {
    return;
  }
  element.style.scale = "";
  element.style.translate = "";
  element.style.borderRadius = "";
  element.style.overflow = "";
}

/**
 * The spring a cancelled gesture returns on: Material 3's fast spatial
 * spring, stiffness 1400 at a damping ratio of 0.9 (mass 1, so the damping
 * coefficient is 0.9 × 2√1400 ≈ 67).
 */
const SETTLE = { damping: 67, stiffness: 1400, type: "spring" } as const;
/** How long a committed layer's preview styles may outlive its exit. */
const COMMIT_CLEAR_MS = 450;

/**
 * One gesture's preview, from start to commit or cancel. Created at the
 * start with the target, fed progress, and ended exactly once.
 */
export interface Preview {
  cancel(): void;
  commit(): void;
  progress(progress: number, edge: BackEdge): void;
}

function surfaceFor(target: PreviewTarget): Surface {
  return target.kind === "layer" ? target.surface : "push";
}

function underElement(target: PreviewTarget): HTMLElement | null {
  return target.kind === "screen" && target.under !== null
    ? screenElement(target.under)
    : null;
}

export function startPreview(target: PreviewTarget): Preview {
  let last = 0;
  let edge: BackEdge = "none";
  let ended = false;
  const release =
    target.kind === "layer" && target.uncovers ? holdCoverage() : null;
  if (target.kind === "screen" && target.under !== null) {
    previewScreen(target.under);
  }

  const apply = (value: number) => {
    write(target.element, previewFrame(surfaceFor(target), value, edge));
    if (release !== null) {
      // Read by the shell into the screen's own transform.
      setCoverage(1 - value);
    }
    const under = underElement(target);
    if (under !== null) {
      under.style.translate = `${underShift(value)}% 0`;
    }
  };

  return {
    progress(value, from) {
      if (ended) {
        return;
      }
      last = value;
      edge = from;
      apply(value);
    },
    cancel() {
      if (ended) {
        return;
      }
      ended = true;
      animate(last, 0, {
        ...SETTLE,
        onUpdate: apply,
        onComplete: () => {
          clear(target.element);
          clear(underElement(target));
          release?.();
          previewScreen(null);
        },
      });
    },
    commit() {
      if (ended) {
        return;
      }
      ended = true;
      if (release !== null) {
        // Still held: the layer is only now being closed, so its cover is
        // still registered, and handing `coverage` back here would animate
        // the screen LEFT again before it slid home. Finish the slide from
        // where the finger left it; release once the layer is gone.
        animateCoverage(0, false);
      }
      if (target.kind === "screen") {
        // The screen it uncovers is already in place under the finger: the
        // stack must not slide it in again from the left.
        skipNextEnter();
        const under = underElement(target);
        if (under !== null) {
          const from = underShift(last);
          animate(from, 0, {
            ...SETTLE,
            onUpdate: (value) => {
              under.style.translate = `${value}% 0`;
            },
            onComplete: () => clear(under),
          });
        }
      }
      window.setTimeout(() => {
        clear(target.element);
        previewScreen(null);
        release?.();
      }, COMMIT_CLEAR_MS);
    },
  };
}
